package com.neurobs.ccab

import scala.util.matching.Regex

/** One part of a split message
 *
 * @param id Unique Id string identifying a split message
 * @param size The number of sub-messages
 * @param index The index of this part
 * @param content The content of this part
 */
case class SplitMessagePart
(
	id: String,
	size: Int,
	index: Int,
	content: String
) {
	import SplitMessagePart._

	/** The string message representing this part */
	def message: String = s"$delimiter$id$delimiter$size$delimiter$index$delimiter$content"
}

object SplitMessagePart {
	private val delimiter = ':'
	// Note - can't use "dotall" option for matching newlines with '.' because it differs between JVM and Scala.js,
	// so use "\s\S" to match any character, including newlines
	private val pattern: Regex = s"""$delimiter([^:]+)$delimiter(0|[1-9]\\d*)$delimiter(0|[1-9]\\d*)$delimiter([\\s\\S]*)""".r

	/** Indicates if the message is part of a split message */
	def isSplitMessagePart(message: String): Boolean = message.startsWith(delimiter.toString)

	/** Parses a message assumed to be a split message part
	 *
	 * @param message
	 * @return The split message part data, or None if the message format was not valid
	 */
	def apply(message: String): Option[SplitMessagePart] = message match {
		case pattern(id, sizeStr, indexStr, content) =>
			val size = sizeStr.toInt
			val index = indexStr.toInt
			if ((size > 0) && (index >= 0) && (index < size))
				Some(SplitMessagePart(id, size, index, content))
			else
				None
		case _ => None
	}
}

/** Possibly incomplete sequence of split message parts, starting with the index 0 part */
case class SplitMessage(parts: Seq[SplitMessagePart]) {
	import SplitMessage._

	/** Returns complete message if complete, or None if incomplete */
	def message: Option[String] =
		if (parts.isEmpty || (parts.size < parts.head.size)) None
		else {
			//println(s"split message ${parts.size} parts")
			Some(parts.foldLeft("")(_ + _.content))
		}

	/** Creates new SplitMessage from this one plus an additional part
	 *
	 * If the next part is not part of the current message, the returned
	 * message is for the new part. If the part is out of order, an
	 * empty message is returned. If the part is for a new message but is not
	 * the first part, an empty message is returned.
	 *
	 * @param next The next part received
	 * @return New split message state
	 */
	def update(next: SplitMessagePart): SplitMessage = {
		if (parts.isEmpty || parts.head.id != next.id) {
			if (next.index == 0) SplitMessage(Seq(next))
			else empty
		}
		else if (next.index == parts.size) SplitMessage(parts :+ next)
		else empty
	}
}

object SplitMessage {
	val empty = SplitMessage(Seq())
}

/** Handles recombination of split messages. Add this as a layer in the message
 * handling processing before parsing of message structure. */
class SplitMessageReceiver {
	private var split = SplitMessage.empty

	/** Processes incoming messages to handle split messages. Normal
	 * non-slit messages are simply passed through.
	 *
	 * @param message the next message packet received
	 * @return Normal message, completed split message, or None if
	 *         the split message is not yet complete, or a split message
	 *         part was invalid.
	 */
	def process(message: String): Option[String] = {
		import SplitMessagePart._
		if (!isSplitMessagePart(message)) {
			split = SplitMessage.empty
			Some(message)
		}
		else SplitMessagePart(message) flatMap { next =>
			split = split.update(next)
			split.message match {
				case None => None
				case Some(m) =>
					split = SplitMessage.empty
					Some(m)
			}
		}
	}
}